import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { WHATSAPP_DISPLAY, WHATSAPP_LINK } from "@/lib/api";
import { cn } from "@/lib/utils";

export const Navbar = () => {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const links = [
    ["/vehicles", t("nav_vehicles")],
    ["/sourcing", t("nav_sourcing")],
    ["/sell", t("nav_sell")],
    ["/about", t("nav_about")],
    ["/contact", t("nav_contact")],
  ];

  return (
    <header
      data-testid="navbar"
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500",
        scrolled || open ? "bg-[#070809]/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent",
      )}
    >
      <div className="container-x flex h-16 items-center justify-between md:h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-2">
          <span className="display text-base tracking-tight md:text-lg">Palissiere</span>
          <span className="mono hidden text-[0.6rem] text-[#8b95a5] sm:inline">Motor Group</span>
        </Link>
        <nav className="hidden items-center gap-8 lg:flex">
          {links.map(([to, label]) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-link-${to.slice(1)}`}
              className={({ isActive }) => cn("mono link-underline text-[0.68rem] transition-colors", isActive ? "text-white" : "text-[#c7ccd4] hover:text-white")}
            >
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-4">
          <div className="mono flex items-center gap-1 text-[0.65rem]" data-testid="language-toggle">
            {["fr", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => setLang(l)}
                className={cn("px-1.5 py-1 transition-colors", lang === l ? "text-white" : "text-[#6f7986] hover:text-[#c7ccd4]")}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="hidden md:block">
            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="nav-whatsapp" className="btn btn-ghost !py-2.5 !px-4">
              {WHATSAPP_DISPLAY}
            </a>
          </div>
          <button data-testid="nav-menu-toggle" onClick={() => setOpen((o) => !o)} className="p-2 lg:hidden" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      <div className={cn("overflow-hidden transition-[max-height] duration-500 lg:hidden", open ? "max-h-[480px]" : "max-h-0")}>
        <nav className="container-x flex flex-col gap-1 pb-8 pt-2" data-testid="mobile-menu">
          {links.map(([to, label]) => (
            <NavLink key={to} to={to} className="display-tight border-b border-white/10 py-4 text-2xl text-white">
              {label}
            </NavLink>
          ))}
          <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="btn btn-whatsapp mt-6">
            WhatsApp · {WHATSAPP_DISPLAY}
          </a>
        </nav>
      </div>
    </header>
  );
};
